"use client";

/**
 * La sección de planes: la única isla cliente de la landing.
 *
 * Todo lo demás de la vitrina es Server Component; acá hay estado —el ciclo de
 * facturación— y por eso vive en su propio archivo con `"use client"`, siguiendo el
 * patrón de hermanos que ya usa el login (ADR-001). El static export no se ve afectado:
 * el HTML se pre-renderiza y esta isla se hidrata.
 *
 * Tres planes, no dos: sin un plan de entrada gratis no hay por dónde empezar, y antes
 * el escalón más bajo eran $14.990.
 *
 * El descuento anual se comunica como **el total del año** («$149.904 al año, de una
 * vez») y no como porcentaje: «-17 %» no le dice nada a quien paga de su bolsillo. Hubo
 * una píldora de «dos meses gratis» y se sacó por pedido directo.
 */

import { useState } from "react";
import { Boton, Container, Etiqueta, Eyebrow, Lista, Titulo } from "@/components/brand/pincel";
import { COLOR, Semitono } from "@/components/brand/elementos";
import { clp, CORREO_CONTACTO, PLANES, PLANES_NOTA, type Ciclo } from "@/components/brand/mock";

export function PlanesLanding({ destino }: { destino: string }) {
  const [ciclo, setCiclo] = useState<Ciclo>("mensual");

  return (
    <section id="planes" className="relative overflow-hidden border-y-2 border-line bg-panel/40 py-16 sm:py-20">
      <Container className="relative">
        <div className="max-w-2xl">
          <Eyebrow>Planes</Eyebrow>
          <Titulo className="mt-4">Menos que un arbitraje por fecha</Titulo>
          <p className="mt-6 leading-relaxed text-muted">
            Un precio, toda la temporada: sin costo por equipo ni por jugador. Lo que pagas es correr la liga
            completa, con la vista pública incluida para todos tus hinchas.
          </p>
        </div>

        {/* Selector de ciclo */}
        <div className="mt-8 flex flex-wrap items-center gap-3">
          <div className="inline-flex rounded-full border-2 border-line bg-panel p-1">
            {(["mensual", "anual"] as const).map((c) => {
              const activo = ciclo === c;
              return (
                <button
                  key={c}
                  type="button"
                  onClick={() => setCiclo(c)}
                  aria-pressed={activo}
                  /* min-h-11: el área táctil de 44px del ADR-004; con py-2 quedaba en 32px */
                  className="font-display min-h-11 rounded-full px-6 text-[11px] uppercase tracking-[0.16em] transition"
                  style={
                    activo
                      ? { background: COLOR.accent, color: "var(--ld-onbright)" }
                      : { color: "var(--ld-muted)" }
                  }
                >
                  {c === "mensual" ? "Mes a mes" : "Anual"}
                </button>
              );
            })}
          </div>
        </div>

        <div className="mt-10 grid items-start gap-5 lg:grid-cols-3">
          {PLANES.map((p) => {
            const valor = ciclo === "anual" ? p.anual : p.mensual;
            return (
              <article
                key={p.nombre}
                className={`relative overflow-hidden rounded-2xl border-2 bg-panel p-7 shadow-lg ${
                  p.destacado ? "border-accent lg:-mt-4 lg:pb-9 lg:pt-9" : "border-line"
                }`}
              >
                {p.destacado && (
                  <>
                    <Semitono tono="accent" className="absolute -right-6 -top-6 h-32 w-44 opacity-25" desde="right" />
                    <span className="absolute right-6 top-6">
                      <Etiqueta tono="accent2" icono="estrella">
                        Popular
                      </Etiqueta>
                    </span>
                  </>
                )}

                <h3 className="font-display relative text-2xl uppercase leading-none text-ink">{p.nombre}</h3>
                <p className="relative mt-2 min-h-[2.5rem] text-sm text-muted">{p.desc}</p>

                <p className="relative mt-6 flex items-baseline gap-1.5">
                  <span className="font-display text-[42px] leading-none text-ink">
                    {valor === 0 ? "Gratis" : clp(valor)}
                  </span>
                  {valor > 0 && <span className="text-sm text-muted">/mes</span>}
                </p>
                <p className="relative mt-1.5 h-4 text-[11px] text-muted">
                  {valor > 0 && ciclo === "anual" ? `${clp(valor * 12)} al año, de una vez` : null}
                  {valor > 0 && ciclo === "mensual" ? "Se cancela cuando quieras" : null}
                </p>

                <div className="relative mt-6 border-t border-line pt-6">
                  <Lista items={p.features} tono={p.destacado ? "accent" : "accent2"} />
                </div>

                {/* «Hablemos» abre el correo con el asunto puesto: quien atiende sabe de entrada
                    que es una asociación preguntando por su plan, y no hace falta una segunda
                    ronda para averiguarlo. */}
                <Boton
                  href={
                    p.destino === "contacto"
                      ? `mailto:${CORREO_CONTACTO}?subject=${encodeURIComponent("Consulta por el plan Asociación")}`
                      : destino
                  }
                  tono={p.destacado ? "accent" : "ink"}
                  variante={p.destacado ? "solido" : "contorno"}
                  className="relative mt-8 w-full"
                >
                  {p.cta}
                </Boton>
              </article>
            );
          })}
        </div>

        <p className="mt-8 max-w-2xl text-xs leading-relaxed text-muted">{PLANES_NOTA}</p>
      </Container>
    </section>
  );
}
